import { gridDefaultPixelDensity } from 'components/editor/shared/gridConfig';
import { MarginSide } from '../models/MarginSide.enum';
import { FormattedInput } from './FormattedInput';
import './MarginSettings.less';

interface Margin {
  top: number;
  left: number;
  right: number;
  bottom: number;
}

interface MarginSettingsViewProps {
  margin: Margin;
  canvasWidth: number;
  canvasHeight: number;
  previewWidth: number;
  previewHeight: number;
  onChange: (value: string | number, position: MarginSide) => void;
  idPrefix?: string;
}

const DECIMAL_PLACES = 1;

export function MarginSettingsView({
  margin,
  canvasWidth,
  canvasHeight,
  previewWidth,
  previewHeight,
  onChange,
  idPrefix = '',
}: MarginSettingsViewProps) {
  const { top, left, right, bottom } = margin;
  const maxWidthInInches = canvasWidth / gridDefaultPixelDensity;
  const maxHeightInInches = canvasHeight / gridDefaultPixelDensity;

  const topValue = (top / gridDefaultPixelDensity).toFixed(DECIMAL_PLACES);
  const leftValue = (left / gridDefaultPixelDensity).toFixed(DECIMAL_PLACES);
  const rightValue = (right / gridDefaultPixelDensity).toFixed(DECIMAL_PLACES);
  const bottomValue = (bottom / gridDefaultPixelDensity).toFixed(DECIMAL_PLACES);

  const prefix = idPrefix ? `${idPrefix}-` : '';

  const marginLineStyle = (value: number, gridValue: number, previewSize: number, side: string) => ({
    [side]: `${(value / gridValue) * previewSize}px`,
  });

  return (
    <div className="page-preview-container">
      <FormattedInput
        key={`top-${topValue}`}
        id={`${prefix}top-margin`}
        variant="standard"
        value={topValue}
        onChange={onChange}
        className="page-preview-container__top-margin number-input_hide-arrows"
        min={0}
        max={maxHeightInInches}
        position={MarginSide.TOP}
      />
      <div className="margin-preview-box">
        <FormattedInput
          key={`left-${leftValue}`}
          id={`${prefix}left-margin`}
          className="page-preview-container__left-margin mui-override number-input_hide-arrows"
          variant="standard"
          value={leftValue}
          onChange={onChange}
          min={0}
          max={maxWidthInInches}
          position={MarginSide.LEFT}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min: 0, max: maxWidthInInches }}
        />
        <div className="margin-preview" style={{ width: previewWidth, height: previewHeight }}>
          <div className="margin-line top" style={marginLineStyle(top, canvasHeight, previewHeight, 'top')} />
          <div className="margin-line right" style={marginLineStyle(right, canvasWidth, previewWidth, 'right')} />
          <div className="margin-line bottom" style={marginLineStyle(bottom, canvasHeight, previewHeight, 'bottom')} />
          <div className="margin-line left" style={marginLineStyle(left, canvasWidth, previewWidth, 'left')} />
          <div className="page-preview" />
        </div>
        <FormattedInput
          key={`right-${rightValue}`}
          id={`${prefix}right-margin`}
          className="page-preview-container__right-margin mui-override number-input_hide-arrows"
          variant="standard"
          value={rightValue}
          onChange={onChange}
          min={0}
          max={maxWidthInInches}
          position={MarginSide.RIGHT}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min: 0, max: maxWidthInInches }}
        />
      </div>
      <FormattedInput
        key={`bottom-${bottomValue}`}
        id={`${prefix}bottom-margin`}
        className="page-preview-container__bottom-margin number-input_hide-arrows"
        variant="standard"
        value={bottomValue}
        onChange={onChange}
        min={0}
        max={maxHeightInInches}
        position={MarginSide.BOTTOM}
      />
    </div>
  );
}
